import {FormControl, FormGroup} from '@angular/forms';
import {FormValue, zNumberToString} from '@brunata-angular/util';
import {SelectableItem} from '@brunata-angular/ui';
import {z} from 'zod';
import {Duration} from 'luxon';

export const vendorModel = z.object({
    vendorModelId: zNumberToString,
    meterType: zNumberToString,
    modelName: z.string(),
    numberOfMeters: z.number(),
});
export type VendorModel = z.infer<typeof vendorModel>;

export const eventAlarmSchema = z
    .object({
        subscriptionId: zNumberToString,
        eventTypeId: zNumberToString,
        throttle: z.number(),
        meterModelCriteriaMismatch: z.boolean(),
        alarmName: z
            .string()
            .nullable()
            .transform(value => value ?? ''),
        recipients: z.array(z.string()),
        vendorModels: z.array(vendorModel),
    })
    .transform(data => ({
        ...data,
        throttleInHours: Duration.fromObject({minutes: data.throttle}).as('hours'),
    }));

export type EventAlarm = z.infer<typeof eventAlarmSchema>;

export type EventAlarmWithTranslations = Omit<EventAlarm, 'throttleInHours'> & {
    usageTranslated: string;
    unitTranslated: string;
};

export type EventAlarmCreateRequest = {
    eventTypeId: number;
    throttle: number;
    alarmName: string;
    recipients: string[];
    vendorModelIds: string[];
};
export type EventAlarmUpdateRequest = Omit<EventAlarmCreateRequest, 'eventTypeId'>;

export type EventAlarmFilterFormGroup = FormGroup<{
    meterModelCriteriaMismatch: FormControl<SelectableItem[]>;
    eventTypeId: FormControl<SelectableItem[]>;
    vendorModels: FormControl<SelectableItem[]>;
    alarmName: FormControl<string>;
}>;

export const eventAlarmTemplateSchema = z.object({
    typeName: z.string(),
    typeDescription: z.string(),
    eventTypeId: zNumberToString,
    categoryName: z
        .string()
        .nullable()
        .transform(value => value ?? ''),
    durationType: z.string(),
});

export type EventAlarmTemplate = z.infer<typeof eventAlarmTemplateSchema>;

export type EventAlarmFormGroup = FormGroup<{
    submitted: FormControl<boolean>;
    throttleInHours: FormControl<number | null>;
    alarmName: FormControl<string>;
    recipients: FormControl<string[]>;
    vendorModels: FormControl<FormValue<VendorModelsFormGroup>[]>;
}>;

export type VendorModelsFormGroup = FormGroup<{
    vendorModelId: FormControl<string>;
    meterType: FormControl<string>;
    modelName: FormControl<string>;
    numberOfMeters: FormControl<number>;
    selected: FormControl<boolean>;
    isEventTypeSupported: FormControl<boolean>;
}>;
